import type { ApiSubtaskAutocompleteRow } from '@/utils/onboardingSubtasksMergeFromApi';

function normalizeOnboardingTasksArray(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) {
    return data.filter(
      (x): x is Record<string, unknown> => Boolean(x && typeof x === 'object' && !Array.isArray(x))
    );
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const inner = o.tasks ?? o.taskList ?? o.data ?? o.results ?? o.items;
    return normalizeOnboardingTasksArray(inner);
  }
  return [];
}

function taskRowType(row: Record<string, unknown>): 'subtask' | 'task' | 'unknown' {
  const t = String(row.type ?? '').trim().toLowerCase();
  if (t === 'subtask') return 'subtask';
  if (t === 'task') return 'task';
  return 'unknown';
}

function rowNumericId(row: Record<string, unknown>): number | undefined {
  const raw = row.taskId ?? row.id;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function rowLooksLinkedToParent(row: Record<string, unknown>, parentTaskId: number): boolean {
  const keys = ['parentTaskId', 'parentId', 'taskParentId'];
  for (const k of keys) {
    const n = Number(row[k]);
    if (Number.isFinite(n) && n === parentTaskId) return true;
  }
  return false;
}

function rowToAutocomplete(row: Record<string, unknown>): ApiSubtaskAutocompleteRow | null {
  const tid = rowNumericId(row);
  if (tid == null) return null;
  const taskTitle = String(row.taskTitle ?? row.title ?? row.name ?? '').trim();
  const completed =
    typeof row.completed === 'boolean'
      ? row.completed
      : String(row.completed).toLowerCase() === 'true' ||
        String(row.status ?? '').toUpperCase() === 'COMPLETE';
  return { taskId: tid, taskTitle, completed: Boolean(completed) };
}

/**
 * Derives onboarding v2 autocomplete-shaped rows from **GET `/tasks?identityId=`** JSON for a CMS parent row.
 *
 * Prefers explicit `parentTaskId`/`parentId`/`taskParentId` on `{ type:"subtask" }` rows. Otherwise scans the flat
 * list for the parent `{ taskId }` anchor and gathers consecutive **`type:"subtask"`** rows immediately before
 * **or** immediately after it (handles subtasks ordered before the parent row).
 *
 * Never calls `/subtasks` — aligns with onboarding v2 “tasks + tasks/complete only” usage.
 */
export function tasksPayloadSubtasksForParent(
  data: unknown,
  parentTaskId: number
): ApiSubtaskAutocompleteRow[] {
  const pid = Number(parentTaskId);
  if (!Number.isFinite(pid)) return [];

  const arr = normalizeOnboardingTasksArray(data);

  const linked = arr.filter((o) => taskRowType(o) === 'subtask' && rowLooksLinkedToParent(o, pid));
  if (linked.length > 0) {
    return linked
      .map((o) => rowToAutocomplete(o))
      .filter((x): x is ApiSubtaskAutocompleteRow => Boolean(x));
  }

  let pi =
    arr.findIndex((o) => rowNumericId(o) === pid && taskRowType(o) !== 'subtask');

  if (pi < 0) {
    pi = arr.findIndex((o) => rowNumericId(o) === pid && taskRowType(o) === 'task');
  }

  /** Relay may omit `type` — match id only once we have exhausted typed anchors. */
  if (pi < 0) {
    pi = arr.findIndex((o) => rowNumericId(o) === pid);
  }

  if (pi < 0) return [];

  const candidates: Record<string, unknown>[] = [];

  for (let i = pi - 1; i >= 0; i--) {
    const o = arr[i];
    if (taskRowType(o) === 'subtask') candidates.unshift(o);
    else break;
  }

  for (let i = pi + 1; i < arr.length; i++) {
    const o = arr[i];
    const rt = taskRowType(o);
    if (rt === 'subtask') {
      candidates.push(o);
      continue;
    }
    if (rt === 'task') break;
    break;
  }

  return candidates
    .map((o) => rowToAutocomplete(o))
    .filter((x): x is ApiSubtaskAutocompleteRow => Boolean(x));
}
